import { useEffect, useMemo, useState } from "react";
import { api, euro, formatApiError, formatDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, FileText, Paperclip, Sparkles, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_LABELS = {
  audifonos: "Audífonos / Material",
  luz: "Luz",
  agua: "Agua",
  alquiler: "Alquiler",
  telefono: "Teléfono",
  internet: "Internet",
  varios: "Varios",
  autonomo: "Autónomo",
  seguridad_social: "Seguridad Social",
  sueldo: "Sueldo",
};

export const PAYMENT_METHOD_LABELS = {
  giro_30: "Giro a 30 días",
  giro_60: "Giro a 60 días",
  domiciliacion: "Domiciliación bancaria",
  transferencia: "Transferencia",
  tarjeta: "Tarjeta",
  efectivo: "Efectivo",
  otro: "Otro",
};

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};
const round2 = (v) => Math.round(v * 100) / 100;
const computeTotal = (lines) =>
  round2(lines.reduce((acc, l) => acc + num(l.base) + num(l.iva_amount) + num(l.rec_amount), 0));

const emptyTaxLine = { base: "", iva_rate: 21, iva_amount: "", rec_rate: 0, rec_amount: "" };
const makeEmptyForm = () => ({
  supplier_id: "",
  center_id: "",
  category: "audifonos",
  invoice_number: "",
  date: new Date().toISOString().slice(0, 10),
  notes: "",
  payment_status: "pending",
  payment_method: "",
  bank_account: "",
  tax_lines: [{ ...emptyTaxLine }],
  total: "",
});

export default function SupplierInvoices() {
  const { user, centers } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [centerFilter, setCenterFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [totalTouched, setTotalTouched] = useState(false);
  const [form, setForm] = useState(makeEmptyForm());
  const [file, setFile] = useState(null);
  const [invoiceLines, setInvoiceLines] = useState([]);
  const [pendingNotes, setPendingNotes] = useState([]);
  const [selectedNoteIds, setSelectedNoteIds] = useState([]);

  const load = async () => {
    const params = {};
    if (["admin", "center_admin"].includes(user?.role) && centerFilter !== "all") params.center_id = centerFilter;
    const [inv, sup] = await Promise.all([
      api.get("/supplier-invoices", { params }).then((r) => r.data),
      api.get("/suppliers").then((r) => r.data),
    ]);
    setInvoices(inv);
    setSuppliers(sup);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [centerFilter]);

  // Albaranes pendientes del proveedor seleccionado (para vincular con la factura)
  useEffect(() => {
    if (!open || !form.supplier_id) {
      setPendingNotes([]);
      setSelectedNoteIds([]);
      return;
    }
    api
      .get(`/delivery-notes/pending/${form.supplier_id}`)
      .then((r) => setPendingNotes(r.data || []))
      .catch(() => setPendingNotes([]));
    setSelectedNoteIds([]);
    // eslint-disable-next-line
  }, [open, form.supplier_id]);

  const baseSum = useMemo(
    () => round2(form.tax_lines.reduce((acc, l) => acc + num(l.base), 0)),
    [form.tax_lines],
  );
  const ivaSum = useMemo(
    () => round2(form.tax_lines.reduce((acc, l) => acc + num(l.iva_amount), 0)),
    [form.tax_lines],
  );
  const recSum = useMemo(
    () => round2(form.tax_lines.reduce((acc, l) => acc + num(l.rec_amount), 0)),
    [form.tax_lines],
  );

  const setTaxLine = (idx, key, value) => {
    setForm((f) => {
      const lines = f.tax_lines.map((l, i) => {
        if (i !== idx) return l;
        const updated = { ...l, [key]: value };
        if (key === "base" || key === "iva_rate") {
          updated.iva_amount = round2(num(key === "base" ? value : updated.base) * num(updated.iva_rate) / 100) || "";
          if (key === "base") {
            updated.rec_amount = num(updated.rec_rate) ? round2(num(value) * num(updated.rec_rate) / 100) : updated.rec_amount;
          }
        }
        if (key === "rec_rate") {
          updated.rec_amount = num(value) ? round2(num(updated.base) * num(value) / 100) : 0;
        }
        return updated;
      });
      return { ...f, tax_lines: lines, total: totalTouched ? f.total : computeTotal(lines) };
    });
  };
  const addTaxLine = () => setForm((f) => ({ ...f, tax_lines: [...f.tax_lines, { ...emptyTaxLine }] }));
  const removeTaxLine = (idx) =>
    setForm((f) => {
      const lines = f.tax_lines.filter((_, i) => i !== idx);
      const safe = lines.length ? lines : [{ ...emptyTaxLine }];
      return { ...f, tax_lines: safe, total: totalTouched ? f.total : computeTotal(safe) };
    });

  const applyParsed = (d) => {
    const mappedLines =
      Array.isArray(d.tax_lines) && d.tax_lines.length
        ? d.tax_lines.map((l) => ({
            base: num(l.base) || "",
            iva_rate: num(l.iva_rate),
            iva_amount: num(l.iva_amount) || "",
            rec_rate: num(l.rec_rate),
            rec_amount: num(l.rec_amount) || "",
          }))
        : null;
    setTotalTouched(false);
    if (Array.isArray(d.lines)) {
      setInvoiceLines(d.lines.filter((l) => l && (l.description || l.brand || l.model)));
    }
    setForm((f) => ({
      ...f,
      invoice_number: d.invoice_number || f.invoice_number,
      date: /^\d{4}-\d{2}-\d{2}/.test(d.date || "") ? d.date.slice(0, 10) : f.date,
      supplier_id: d.supplier_id || f.supplier_id,
      payment_method: PAYMENT_METHOD_LABELS[d.payment_method] ? d.payment_method : f.payment_method,
      bank_account: d.bank_account || f.bank_account,
      notes: d.notes || f.notes,
      tax_lines: mappedLines || f.tax_lines,
      total: num(d.total) ? round2(num(d.total)) : mappedLines ? computeTotal(mappedLines) : f.total,
    }));
    if (!d.supplier_id && d.supplier_name) {
      toast.info(`Proveedor detectado: "${d.supplier_name}". No está en tu lista, selecciónalo o créalo.`);
    }
  };

  const onFileSelected = async (f) => {
    setFile(f);
    if (!f) return;
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("doc_type", "invoice");
      const { data } = await api.post("/supplier-docs/parse", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      applyParsed(data);
      toast.success("Documento analizado: revisa y corrige los campos si hace falta");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setParsing(false);
    }
  };

  const resetForm = () => {
    setForm(makeEmptyForm());
    setFile(null);
    setTotalTouched(false);
    setInvoiceLines([]);
    setPendingNotes([]);
    setSelectedNoteIds([]);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const cleanLines = form.tax_lines
        .filter((l) => num(l.base) || num(l.iva_amount) || num(l.rec_amount))
        .map((l) => ({
          base: num(l.base),
          iva_rate: num(l.iva_rate),
          iva_amount: num(l.iva_amount),
          rec_rate: num(l.rec_rate),
          rec_amount: num(l.rec_amount),
        }));
      const data = new FormData();
      data.append("supplier_id", form.supplier_id || "");
      data.append("center_id", form.center_id || user?.center_id || centers[0]?.id || "");
      data.append("category", form.category || "audifonos");
      data.append("invoice_number", form.invoice_number);
      data.append("date", form.date);
      data.append("amount", baseSum);
      data.append("iva_rate", cleanLines[0]?.iva_rate ?? 21);
      data.append("tax_lines", JSON.stringify(cleanLines));
      data.append("total", form.total !== "" ? num(form.total) : computeTotal(form.tax_lines));
      data.append("payment_method", form.payment_method || "");
      data.append("bank_account", form.bank_account || "");
      data.append("invoice_lines", JSON.stringify(invoiceLines));
      data.append("delivery_note_ids", JSON.stringify(selectedNoteIds));
      data.append("notes", form.notes || "");
      data.append("payment_status", form.payment_status || "pending");
      data.append("paid", form.payment_status === "paid" ? "true" : "false");
      if (file) data.append("file", file);
      const { data: created } = await api.post("/supplier-invoices", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Factura de proveedor registrada");
      if (created?.link_result?.has_discrepancy) {
        const msgs = (created.link_result.discrepancies || []).map((d) => d.message).filter(Boolean);
        toast.warning(
          `El proveedor te ha facturado algo distinto a lo recibido:\n${msgs.map((m) => `• ${m}`).join("\n")}`,
          { duration: 12000 },
        );
      } else if (created?.link_result) {
        toast.success("Albaranes vinculados: todo cuadra con la factura");
      }
      setOpen(false);
      resetForm();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar factura?")) return;
    await api.delete(`/supplier-invoices/${id}`);
    load();
  };

  const downloadAttachment = async (inv) => {
    try {
      const resp = await api.get(`/supplier-invoices/${inv.id}/attachment`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      window.open(url, "_blank");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const updateStatus = async (inv, value) => {
    try {
      await api.patch(`/supplier-invoices/${inv.id}/status`, { payment_status: value });
      toast.success("Estado actualizado");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const supplierName = (id) => suppliers.find((s) => s.id === id)?.name || "—";
  const centerName = (id) => centers.find((c) => c.id === id)?.name || "—";

  const STATUS_META = {
    pending: { label: "Pendiente", className: "bg-[#F3E5D8] text-[#2A2F2C]" },
    paid: { label: "Pagada", className: "bg-[#D1E0D7] text-[#2A2F2C]" },
    returned: { label: "Devuelta", className: "bg-[#F4D8D2] text-[#A23F2D]" },
  };
  const statusOf = (inv) => inv.payment_status || (inv.paid ? "paid" : "pending");

  const showBankAccount = ["giro_30", "giro_60", "domiciliacion", "transferencia"].includes(form.payment_method);

  return (
    <div className="space-y-6" data-testid="supplier-invoices-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[#6E7772] font-semibold">
            Gastos
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-[#2A2F2C] mt-1">
            Facturas de proveedores
          </h1>
          <p className="text-sm text-[#6E7772] mt-1">
            Sube el PDF o la foto de la factura y la IA rellenará los campos automáticamente.
          </p>
        </div>
        <div className="flex gap-3">
          {["admin", "center_admin"].includes(user?.role) && (
            <div className="w-56">
              <Select value={centerFilter} onValueChange={setCenterFilter}>
                <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los centros</SelectItem>
                  {centers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="new-supplier-invoice-btn" className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white">
                <Plus className="h-4 w-4 mr-2" /> Registrar factura
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">Nueva factura de proveedor</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Subida + auto-relleno IA */}
                <div className="md:col-span-2 border-2 border-dashed border-[#C9D3CB] rounded-lg p-4 bg-[#F6F7F4]">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="h-4 w-4 text-[#4A5D4E]" />
                    <Label className="mb-0 font-medium">
                      Adjunta la factura (PDF / imagen) y se rellenará automáticamente
                    </Label>
                  </div>
                  <Input
                    type="file"
                    accept=".pdf,image/*"
                    disabled={parsing}
                    onChange={(e) => onFileSelected(e.target.files?.[0] || null)}
                    data-testid="supplier-invoice-file"
                  />
                  {parsing && (
                    <div className="flex items-center gap-2 mt-2 text-sm text-[#4A5D4E]" data-testid="parse-doc-spinner">
                      <Loader2 className="h-4 w-4 animate-spin" /> Analizando documento con IA…
                    </div>
                  )}
                  {file && !parsing && (
                    <div className="text-xs text-[#6E7772] mt-1 flex items-center gap-1">
                      <Paperclip className="h-3 w-3" /> {file.name}
                    </div>
                  )}
                </div>

                <div className="md:col-span-2">
                  <Label>Categoría del gasto *</Label>
                  <Select
                    value={form.category}
                    onValueChange={(v) => setForm({ ...form, category: v })}
                  >
                    <SelectTrigger data-testid="category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="audifonos">Audífonos / Material clínico</SelectItem>
                      <SelectItem value="luz">Luz</SelectItem>
                      <SelectItem value="agua">Agua</SelectItem>
                      <SelectItem value="alquiler">Alquiler</SelectItem>
                      <SelectItem value="telefono">Teléfono</SelectItem>
                      <SelectItem value="internet">Internet</SelectItem>
                      <SelectItem value="varios">Varios</SelectItem>
                      <SelectItem value="autonomo">Autónomo</SelectItem>
                      <SelectItem value="seguridad_social">Seguridad Social</SelectItem>
                      <SelectItem value="sueldo">Sueldo trabajadores</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Proveedor / Acreedor</Label>
                  <Select
                    value={form.supplier_id || "none"}
                    onValueChange={(v) => setForm({ ...form, supplier_id: v === "none" ? "" : v })}
                  >
                    <SelectTrigger data-testid="supplier-select"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin proveedor</SelectItem>
                      {suppliers.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Centro *</Label>
                  {["admin", "center_admin"].includes(user?.role) ? (
                    <Select value={form.center_id} onValueChange={(v) => setForm({ ...form, center_id: v })}>
                      <SelectTrigger><SelectValue placeholder="Selecciona" /></SelectTrigger>
                      <SelectContent>
                        {centers.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input value={centerName(user?.center_id)} disabled />
                  )}
                </div>
                {form.supplier_id && pendingNotes.length > 0 && (
                  <div className="md:col-span-2 border border-[#E5E3DB] rounded-lg overflow-hidden" data-testid="link-delivery-notes-section">
                    <div className="px-3 py-2 bg-[#FAF9F6] text-sm font-medium text-[#2A2F2C]">
                      Vincular albaranes pendientes de este proveedor
                    </div>
                    <div className="p-3 space-y-2">
                      <p className="text-xs text-[#6E7772]">
                        Marca los albaranes que corresponden a esta factura. Al guardar se compararán las líneas
                        y si el proveedor te ha facturado algo mal recibirás una alerta.
                      </p>
                      {pendingNotes.map((n) => {
                        const dnTotal = (n.lines || []).reduce(
                          (a, l) => a + (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0),
                          0,
                        );
                        return (
                          <label
                            key={n.id}
                            className="flex items-center gap-2 text-sm cursor-pointer border border-[#E5E3DB] rounded px-2 py-1.5 hover:bg-[#FAF9F6]"
                            data-testid={`link-dn-${n.id}`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedNoteIds.includes(n.id)}
                              onChange={(e) =>
                                setSelectedNoteIds((prev) =>
                                  e.target.checked ? [...prev, n.id] : prev.filter((x) => x !== n.id),
                                )
                              }
                            />
                            <span className="font-mono">{n.note_number || n.id.slice(0, 8)}</span>
                            <span className="text-[#6E7772]">{formatDate(n.date)}</span>
                            <span className="text-[#6E7772]">{(n.lines || []).length} línea(s)</span>
                            <span className="ml-auto font-medium">{euro(dnTotal)}</span>
                          </label>
                        );
                      })}
                      {invoiceLines.length > 0 && (
                        <div className="text-xs text-[#4A5D4E]" data-testid="detected-invoice-lines">
                          La IA ha detectado {invoiceLines.length} línea(s) de artículos en la factura para la comparación.
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div>
                  <Label>Nº factura</Label>
                  <Input
                    data-testid="supplier-invoice-number"
                    required
                    value={form.invoice_number}
                    onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Fecha</Label>
                  <Input
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                  />
                </div>

                {/* Desglose fiscal */}
                <div className="md:col-span-2 border border-[#E5E3DB] rounded-lg overflow-hidden">
                  <div className="px-3 py-2 bg-[#FAF9F6] text-sm font-medium text-[#2A2F2C]">
                    Desglose fiscal (Base · IVA · Recargo de Equivalencia)
                  </div>
                  <div className="p-3 space-y-2">
                    <div className="hidden md:grid grid-cols-[1fr_1fr_1fr_1fr_1fr_2rem] gap-2 text-xs text-[#6E7772] font-medium">
                      <span>Base (€)</span>
                      <span>Tipo IVA (%)</span>
                      <span>Cantidad IVA (€)</span>
                      <span>REC (%)</span>
                      <span>REC (€)</span>
                      <span></span>
                    </div>
                    {form.tax_lines.map((l, i) => (
                      <div key={i} className="grid grid-cols-2 md:grid-cols-[1fr_1fr_1fr_1fr_1fr_2rem] gap-2 items-center">
                        <Input
                          type="number" step="0.01" placeholder="Base"
                          value={l.base}
                          onChange={(e) => setTaxLine(i, "base", e.target.value)}
                          data-testid={`tax-line-base-${i}`}
                        />
                        <Input
                          type="number" step="0.01" placeholder="% IVA"
                          value={l.iva_rate}
                          onChange={(e) => setTaxLine(i, "iva_rate", e.target.value)}
                          data-testid={`tax-line-iva-rate-${i}`}
                        />
                        <Input
                          type="number" step="0.01" placeholder="€ IVA"
                          value={l.iva_amount}
                          onChange={(e) => setTaxLine(i, "iva_amount", e.target.value)}
                          data-testid={`tax-line-iva-amount-${i}`}
                        />
                        <Input
                          type="number" step="0.01" placeholder="% REC"
                          value={l.rec_rate}
                          onChange={(e) => setTaxLine(i, "rec_rate", e.target.value)}
                          data-testid={`tax-line-rec-rate-${i}`}
                        />
                        <Input
                          type="number" step="0.01" placeholder="€ REC"
                          value={l.rec_amount}
                          onChange={(e) => setTaxLine(i, "rec_amount", e.target.value)}
                          data-testid={`tax-line-rec-amount-${i}`}
                        />
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeTaxLine(i)}>
                          <X className="h-4 w-4 text-[#C07F67]" />
                        </Button>
                      </div>
                    ))}
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 pt-1">
                      <Button type="button" variant="outline" size="sm" onClick={addTaxLine} data-testid="add-tax-line-btn">
                        <Plus className="h-3 w-3 mr-1" /> Añadir tipo de IVA
                      </Button>
                      <div className="text-xs text-[#6E7772]">
                        Bases: {euro(baseSum)} · IVA: {euro(ivaSum)} · REC: {euro(recSum)}
                      </div>
                    </div>
                    <div className="flex items-center justify-end gap-3 border-t border-[#E5E3DB] pt-2">
                      <Label className="mb-0 font-semibold">TOTAL (€)</Label>
                      <Input
                        type="number" step="0.01"
                        className="w-40 font-semibold"
                        value={form.total}
                        onChange={(e) => { setTotalTouched(true); setForm({ ...form, total: e.target.value }); }}
                        data-testid="invoice-total-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Forma de pago al proveedor */}
                <div>
                  <Label>Forma de pago al proveedor</Label>
                  <Select
                    value={form.payment_method || "none"}
                    onValueChange={(v) => setForm({ ...form, payment_method: v === "none" ? "" : v })}
                  >
                    <SelectTrigger data-testid="payment-method-select"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Sin especificar —</SelectItem>
                      <SelectItem value="giro_30">Giro a 30 días</SelectItem>
                      <SelectItem value="giro_60">Giro a 60 días</SelectItem>
                      <SelectItem value="domiciliacion">Domiciliación bancaria</SelectItem>
                      <SelectItem value="transferencia">Transferencia</SelectItem>
                      <SelectItem value="tarjeta">Tarjeta</SelectItem>
                      <SelectItem value="efectivo">Efectivo</SelectItem>
                      <SelectItem value="otro">Otro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {showBankAccount && (
                  <div>
                    <Label>Nº de cuenta / IBAN</Label>
                    <Input
                      value={form.bank_account}
                      onChange={(e) => setForm({ ...form, bank_account: e.target.value })}
                      placeholder="ES00 0000 0000 0000 0000 0000"
                      data-testid="bank-account-input"
                    />
                  </div>
                )}

                <div className="md:col-span-2">
                  <Label>Notas</Label>
                  <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </div>
                <div className="md:col-span-2 flex items-center gap-3">
                  <Label className="mb-0">Estado del pago</Label>
                  <Select
                    value={form.payment_status}
                    onValueChange={(v) => setForm({ ...form, payment_status: v })}
                  >
                    <SelectTrigger className="w-64" data-testid="payment-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pendiente de pagar</SelectItem>
                      <SelectItem value="paid">Pagada</SelectItem>
                      <SelectItem value="returned">Devuelta</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter className="md:col-span-2">
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                  <Button
                    type="submit"
                    disabled={saving || parsing}
                    data-testid="save-supplier-invoice-btn"
                    className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
                  >
                    {saving ? "Guardando…" : "Guardar"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="rounded-lg border border-[#E5E3DB] bg-white">
        <Table>
          <TableHeader>
            <TableRow className="border-[#E5E3DB]">
              <TableHead>Nº</TableHead>
              <TableHead>Fecha</TableHead>
              <TableHead>Categoría</TableHead>
              <TableHead>Proveedor</TableHead>
              <TableHead>Centro</TableHead>
              <TableHead className="text-right">Base</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead>Forma de pago</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} className="text-center text-[#6E7772] py-12">
                  <FileText className="h-8 w-8 mx-auto mb-2 opacity-60" strokeWidth={1.5} />
                  Sin facturas de proveedores.
                </TableCell>
              </TableRow>
            )}
            {invoices.map((inv) => (
              <TableRow key={inv.id} className="border-[#E5E3DB]">
                <TableCell className="font-mono text-sm">{inv.invoice_number}</TableCell>
                <TableCell>{formatDate(inv.date)}</TableCell>
                <TableCell>
                  <Badge className="bg-[#EAE6DF] text-[#2A2F2C]">
                    {CATEGORY_LABELS[inv.category] || inv.category || "—"}
                  </Badge>
                </TableCell>
                <TableCell>{supplierName(inv.supplier_id)}</TableCell>
                <TableCell>{centerName(inv.center_id)}</TableCell>
                <TableCell className="text-right">{euro(inv.amount)}</TableCell>
                <TableCell className="text-right font-medium">{euro(inv.total)}</TableCell>
                <TableCell className="text-sm">
                  {PAYMENT_METHOD_LABELS[inv.payment_method] || "—"}
                  {inv.bank_account && (
                    <div className="text-xs text-[#6E7772] font-mono">{inv.bank_account}</div>
                  )}
                </TableCell>
                <TableCell>
                  <Select
                    value={statusOf(inv)}
                    onValueChange={(v) => updateStatus(inv, v)}
                  >
                    <SelectTrigger
                      className={`h-8 w-36 border-0 ${STATUS_META[statusOf(inv)].className}`}
                      data-testid={`status-select-${inv.id}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pendiente</SelectItem>
                      <SelectItem value="paid">Pagada</SelectItem>
                      <SelectItem value="returned">Devuelta</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="space-x-1">
                  {inv.attachment_path && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => downloadAttachment(inv)}
                      data-testid={`view-attachment-${inv.id}`}
                    >
                      <Paperclip className="h-4 w-4 mr-1" /> Ver
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => remove(inv.id)}
                  >
                    <Trash2 className="h-4 w-4 text-[#C07F67]" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
